import React from "react";

// The LetsLoop doodle vocabulary, as SVG.
//
// Ported from the marketing asset tooling that produced the Instagram posters
// (LL-0189, scripts/make_cutout_poster.py: star / squiggle / burst_lines /
// heart / arrow). Those are drawn with PIL onto a raster canvas, which is right
// for a 1080x1350 post and wrong for a responsive page, so the same shapes are
// re-expressed as SVG here: crisp at any size, a few hundred bytes each,
// themeable through currentColor, and animatable.
//
// This is the layer the first pass of the revamp was missing. The composition
// was structurally correct and completely flat, because it had none of the
// playful marks that make the posters feel like LetsLoop.
//
// Every doodle is decorative: aria-hidden, never load-bearing for meaning, and
// pointer-events: none so it can overlap content without eating clicks.

export type DoodleName =
  | "star"
  | "starFilled"
  | "squiggle"
  | "burst"
  | "heart"
  | "arrow"
  | "loop"
  | "underline";

type Props = {
  name: DoodleName;
  className?: string;
  /** px */
  size?: number;
  /** degrees */
  rotate?: number;
  style?: React.CSSProperties;
};

// Paths are drawn in a 100x100 box so `size` scales uniformly.
// Deliberately a little irregular: perfectly geometric marks read as clip-art,
// and the posters these come from are hand-drawn in feel.
const PATHS: Record<DoodleName, React.ReactNode> = {
  star: (
    <path
      d="M50 8 L59 38 L91 39 L65 57 L74 88 L50 69 L26 88 L35 57 L9 39 L41 38 Z"
      fill="none"
      strokeWidth={8}
      strokeLinejoin="round"
    />
  ),
  starFilled: (
    <path
      d="M50 8 L59 38 L91 39 L65 57 L74 88 L50 69 L26 88 L35 57 L9 39 L41 38 Z"
      strokeWidth={0}
    />
  ),
  squiggle: (
    <path
      d="M4 56 Q19 26 34 56 T64 56 T94 56"
      fill="none"
      strokeWidth={9}
      strokeLinecap="round"
    />
  ),
  burst: (
    <g strokeWidth={8} strokeLinecap="round">
      <path d="M50 6 V26" />
      <path d="M50 74 V94" />
      <path d="M6 50 H26" />
      <path d="M74 50 H94" />
      <path d="M19 19 L33 33" />
      <path d="M67 67 L81 81" />
      <path d="M81 19 L67 33" />
      <path d="M33 67 L19 81" />
    </g>
  ),
  heart: (
    <path
      d="M50 86 C22 66 8 50 8 33 C8 20 18 11 30 11 C39 11 46 16 50 23 C54 16 61 11 70 11 C82 11 92 20 92 33 C92 50 78 66 50 86 Z"
      fill="none"
      strokeWidth={8}
      strokeLinejoin="round"
    />
  ),
  arrow: (
    <g fill="none" strokeWidth={8} strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 72 Q34 20 84 30" />
      <path d="M66 16 L86 29 L70 46" />
    </g>
  ),
  // The brand mark abstracted: two joined loops.
  loop: (
    <g fill="none" strokeWidth={9} strokeLinecap="round">
      <circle cx={30} cy={50} r={22} />
      <circle cx={70} cy={50} r={22} />
    </g>
  ),
  // A rough marker underline, for sitting beneath a word.
  underline: (
    <path
      d="M4 62 Q26 44 50 56 T96 46"
      fill="none"
      strokeWidth={11}
      strokeLinecap="round"
    />
  ),
};

const Doodle: React.FC<Props> = ({
  name,
  className = "",
  size = 40,
  rotate = 0,
  style,
}) => (
  <svg
    aria-hidden="true"
    focusable="false"
    viewBox="0 0 100 100"
    width={size}
    height={size}
    className={`pointer-events-none select-none ${className}`}
    style={{ transform: rotate ? `rotate(${rotate}deg)` : undefined, ...style }}
    stroke="currentColor"
    fill="currentColor"
  >
    {PATHS[name]}
  </svg>
);

export default Doodle;
